import { Component } from "react"
import PropTypes from "prop-types"
import { size } from "lodash"
import { Button } from "@mui/material"
import passwordTester from "owasp-password-strength-test"
import classNames from "classnames"
import { zxcvbn, zxcvbnOptions } from "@zxcvbn-ts/core"
import { adjacencyGraphs, dictionary } from "@zxcvbn-ts/language-common"
import { translations, dictionary as enDictionary } from "@zxcvbn-ts/language-en"
import ConfirmWithMfa from "../confirm-with-mfa"
import Tooltip from "components/material/tooltip"
import { getPasswordStrength, getStrengthText } from "helpers/password"

import "./style.scss"

const tooltipText = <div className="helper-tooltip">
  <h1>Password / Passphrase</h1>
  <p>Provide a strong password between 10-19 characters, that is not commonly used, does not contain 3 or more consecutive repeated characters, and has at least 3 of the following:</p>
  <ul className="password-other-criterias">
    <li>Lower case</li>
    <li>Upper case</li>
    <li>At least one number</li>
    <li>At least one symbol</li>
  </ul>

  <p><b>OR</b></p>
  <p>Provide a passphrase that is any phrase with 20-128 characters and does not contain 3 or more consecutive repeated characters</p>
</div>

function isCommonPassword(password) {
  const options = {
    translations,
    graphs: adjacencyGraphs,
    dictionary: {
      ...dictionary,
      ...enDictionary,
    },
  }

  zxcvbnOptions.setOptions(options)
  const { feedback: { warning } } = zxcvbn(password)
  return warning
}

class Profile extends Component {
  constructor(...args) {
    super(...args)
    this.state = {
      oldPassword: "",
      password: "",
      confirm: "",
    }
  }

  static propTypes = {
    passwordErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
    update: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    mfaEnabled: PropTypes.bool.isRequired,
  }

  onChange = event => {
    const { value, name } = event.target
    let stateChanges = {
      [name]: value,
    }

    if (name === "password") {
      passwordTester.config({
        allowPassphrases: true,
        maxLength: 128,
        minLength: 10,
        minPhraseLength: 20,
        minOptionalTestsToPass: 3,
      })
      const result = passwordTester.test(value)

      const commonMessage = isCommonPassword(value)

      stateChanges = {
        ...stateChanges,
        strength: getPasswordStrength(value, result),
        passwordLength: size(value),
        strongPassword: result.strong,
        commonMessage,
      }
    }

    this.setState(stateChanges)
  }

  onSave = () => {
    const { mfaEnabled, openDialog } = this.props

    if (mfaEnabled) {
      openDialog({
        title: "Confirm MFA",
        content: <ConfirmWithMfa
          message="To change your password, please enter the authentication code of your MFA application."
          save={this.updatePassword}
          saveText="Confirm"
        />,
      })
    } else {
      this.updatePassword()
    }
  }

  updatePassword = (token) => {
    const { update } = this.props

    update("password", "/api/users/me/password", { ...this.state, token }, {
      onSuccess: "Password updated",
    })
  }

  toggleFocus = (newFocus) => () => {
    this.setState({ focus: newFocus })
  }

  render() {
    const { passwordErrors } = this.props

    const { oldPassword, password, confirm, strength, passwordLength, strongPassword, focus, commonMessage } = this.state
    const strengthText = getStrengthText(strength)

    return <div className="password-change">
      {!!passwordErrors.length && <div className="alert alert-danger pl-5">
        {passwordErrors.map((error, i) => <div key={i}>{error}</div>)}
      </div>}
      <div className="form-group">
        <label htmlFor="oldPassword">Old password</label>
        <input
          className="form-control"
          id="oldPassword"
          name="oldPassword"
          type="password"
          value={oldPassword}
          onChange={this.onChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="password">New password</label>
        <Tooltip variant="light" title={tooltipText} arrow placement="left-start">
          <input
            className="form-control"
            id="password"
            type="password"
            name="password"
            value={password}
            onFocus={this.toggleFocus(true)}
            onBlur={this.toggleFocus(false)}
            onChange={this.onChange}
          />
        </Tooltip>
        <div className={classNames("common-password-text", { visible: passwordLength > 0, invisible: !passwordLength })}>{commonMessage}</div>
        <div className="strength-wrapper">
          <div className={classNames("strength-meter mt-2", { visible: passwordLength > 0 || focus, invisible: !passwordLength && !focus })}>
            <div className="strength-meter-fill" data-strength={strength} />
          </div>
          <span className={classNames("strength-text", { visible: passwordLength > 0, invisible: !passwordLength })} data-strength={strength}>{strengthText}</span>
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="confirm">Confirm new password</label>
        <input
          className="form-control"
          id="confirm"
          type="password"
          name="confirm"
          value={confirm}
          onChange={this.onChange}
        />
      </div>
      <Button variant="contained" color="primary" disabled={!strongPassword || commonMessage} className="btn btn-primary pull-right" onClick={this.onSave}>Change password</Button>
    </div>
  }
}

export default Profile
